<template>
    <div class="StepContentEditor">
        <div class="card-editor-container">
            <div class="step-header">
                <div class="text-ellipsis" :title="step.name"><b>{{ step.name }}</b></div>
                <v-flex></v-flex>
                <v-btn icon small class="mr-3" @click="findNodeInGraph">
                    <v-icon small>mdi-crosshairs-gps</v-icon>
                </v-btn>
                <v-switch
                    dense
                    hide-details
                    class="ma-0"
                    v-model="activate"
                    @click="toggleActive"
                    v-if="step.scenario_code !== defaultScenarioCode"
                ></v-switch>
                <v-menu offset-y>
                    <template v-slot:activator="{ on }">
                        <v-btn icon small v-on="on">
                            <v-icon small>mdi-dots-vertical</v-icon>
                        </v-btn>
                    </template>
                    <div class="fci-list">
                        <div class="fci-list-item" @click="editStepName"
                            v-if="step.scenario_code !== defaultScenarioCode">
                            <v-icon small class="mr-2">mdi-pencil</v-icon>
                            {{ $f.tc('rename') }}
                        </div>
                        <div class="fci-list-item" @click="cloneStep">
                            <v-icon small class="mr-2">mdi-content-copy</v-icon>
                            {{ $f.tc('copy') }}
                        </div>
                        <div class="fci-list-item" @click="deleteStep"
                            v-if="step.scenario_code !== defaultScenarioCode">
                            <v-icon small class="mr-2">mdi-delete</v-icon>
                            {{ $f.tc('delete') }}
                        </div>
                    </div>
                </v-menu>
            </div>
            <NavigationIcon @scroll-to-card="scrollToCard($event)"></NavigationIcon>
            <div class="fci-scrollbar step-body" :class="{'wait-drop': waitDrop}" ref="body">
                <StepReminder v-if="step.code === reminderStepCode" :key="step.code"></StepReminder>
                <StepCondition v-if="showStepCondition" :step="step"></StepCondition>
                <div
                    v-if="showStepCondition || step.code === reminderStepCode"
                    style="height: 3px; background: #D0D4E2; margin-top: 30px"
                ></div>
                <div
                    class="drop-zone centerXY"
                    @dragenter="onDragEnter"
                    @dragleave="onDragLeave"
                    @dragover.prevent
                    @drop="onDrop($event, null)"
                >
                    <v-icon small v-if="waitDrop">mdi-plus-circle-outline</v-icon>
                </div>
                <template v-for="card in step.cards">
                    <component
                        :key="card.code"
                        :is="MapCardTypeToComponent[card.card_type_id] || CardDefault"
                        :ref="card.code"
                        :card="card"
                        :highlight="card.code === activeCardCode"
                        :expandMode="false"
                    ></component>
                    <div
                        class="drop-zone centerXY"
                        :key="card.code + '-drop'"
                        @dragenter="onDragEnter"
                        @dragleave="onDragLeave"
                        @dragover.prevent
                        @drop="onDrop($event, card)"
                    >
                        <v-icon small v-if="waitDrop">mdi-plus-circle-outline</v-icon>
                    </div>
                </template>
            </div>
        </div>
        <CardSelector class="card-type-container" @select="addCard"></CardSelector>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import { CardType, MapCardTypeToData } from '@/utils/const'
import { AppEventBus, AppEventBusType } from '@/eventbus/app.eventbus'
import CardText from '@/views/Scenario/StepEditor/Card/CardText'
import CardDefault from '@/views/Scenario/StepEditor/Card/CardDefault'
import CardGoToStep from '@/views/Scenario/StepEditor/Card/CardGoToStep'
import CardQuickReply from '@/views/Scenario/StepEditor/Card/CardQuickReply'
import StepCondition from '@/views/Scenario/StepEditor/StepCondition'
import CardImage from '@/views/Scenario/StepEditor/Card/CardImage'
import CardCarousel from '@/views/Scenario/StepEditor/Card/CardCarousel'
import CardConfig from '@/views/Scenario/StepEditor/Card/CardConfig'
import CardAPI from '@/views/Scenario/StepEditor/Card/CardAPI'
import CardForm from '@/views/Scenario/StepEditor/Card/CardForm'
import CardSelector from '@/views/Scenario/StepEditor/CardSelector'
import CardHumanSupport from '@/views/Scenario/StepEditor/Card/CardHumanSupport'
import CardTag from '@/views/Scenario/StepEditor/Card/CardTag'
import CardTicket from '@/views/Scenario/StepEditor/Card/CardTicket'
import CardZaloList from '@/views/Scenario/StepEditor/Card/CardZaloList'
import CardUserLocation from '@/views/Scenario/StepEditor/Card/CardUserLocation'
import CardLuaScript from '@/views/Scenario/StepEditor/Card/CardLuaScript'
import CardGetCRM from '@/views/Scenario/StepEditor/Card/CardGetCRM'
import CardUpdateCRM from '@/views/Scenario/StepEditor/Card/CardUpdateCRM'
import CardKBTrigger from '@/views/Scenario/StepEditor/Card/CardKBTrigger'
import CardRatingAgent from '@/views/Scenario/StepEditor/Card/CardRatingAgent'
import StepReminder from '@/views/Scenario/StepEditor/StepReminder'
import CardEmail from '@/views/Scenario/StepEditor/Card/CardEmail'
import FormRules from '@/modules/fptai/utils/FormRules'
import CardSuggest from '@/views/Scenario/StepEditor/Card/CardSuggest'
import NavigationIcon from '@/views/Scenario/StepEditor/NavigationIcon'
import CardSmartReminder from './Card/CardSmartReminder.vue'

const MapCardTypeToComponent = {
    [CardType.TEXT]: CardText,
    [CardType.GO_TO_STEP]: CardGoToStep,
    [CardType.QUICK_REPLY]: CardQuickReply,
    [CardType.IMAGE]: CardImage,
    [CardType.CAROUSEL]: CardCarousel,
    [CardType.CONFIG]: CardConfig,
    [CardType.JSON_API]: CardAPI,
    [CardType.FORM]: CardForm,
    [CardType.REQUEST_HUMAN_SUPPORT]: CardHumanSupport,
    [CardType.TAG]: CardTag,
    [CardType.TICKET]: CardTicket,
    [CardType.ZALO_LIST_TEMPLATE]: CardZaloList,
    [CardType.REQUEST_USER_LOCATION]: CardUserLocation,
    [CardType.LUA_SCRIPT]: CardLuaScript,
    [CardType.GET_SENDER_ATTRIBUTE]: CardGetCRM,
    [CardType.UPDATE_SENDER_ATTRIBUTE]: CardUpdateCRM,
    [CardType.EMAIL]: CardEmail,
    [CardType.SMART_REMINDER]: CardSmartReminder,
    [CardType.SUGGEST]: CardSuggest,
    [CardType.KB_TRIGGER]: CardKBTrigger,
    [CardType.RATING]: CardRatingAgent
}

export default {
    name: 'StepContentEditor',
    components: {
        StepReminder,
        CardSelector,
        StepCondition,
        NavigationIcon
    },
    data () {
        return {
            CardType,
            MapCardTypeToComponent,
            CardDefault,
            MapCardTypeToData,
            activate: this.step ? !!this.step.activate : false
        }
    },
    computed: {
        ...mapState({
            reminderStepCode: state => state.scenario.reminderStepCode,
            defaultScenarioCode: state => state.scenario.defaultScenarioCode,
            shouldUpdateGraph: state => state.scenario.shouldUpdateGraph,
            waitDrop: state => state.scenario.draggingCard,
            step: state => state.scenario.activeStep,
            activeCardCode: state => state.scenario.activeCardCode
        }),
        showStepCondition () {
            let stepCode = this.step.code
            let state = this.$store.state.scenario
            return !(
                stepCode === state.reminderStepCode ||
                stepCode === state.startSessionStepCode ||
                stepCode === state.startSentenceStepCode
            )
        }
    },
    methods: {
        scrollToCard (card) {
            let item = this.$refs[card]
            item[0].$el.scrollIntoView()
        },
        addCard (card) {
            card.step_code = this.step.code
            card.position = this.step.cards.length > 0 ? this.step.cards[this.step.cards.length - 1].position + 1 : 0
            this.$store.dispatch(this.$at.scenario.CREATE_CARD, card)
        },
        toggleActive () {
            this.step.activate = this.activate ? 1 : 0
            this.$store.dispatch(this.$at.scenario.UPDATE_STEP, this.step).then(this.$forceUpdate)
        },
        editStepName () {
            this.$appService.useEventBus(AppEventBusType.EDIT_TEXT, {
                text: this.step.name,
                maxLength: 45,
                rules: [FormRules.ruleRequired, FormRules.ruleNoHtmlTag]
            }).then(name => {
                this.$store.dispatch(this.$at.scenario.UPDATE_STEP, {
                    code: this.step.code,
                    name: name,
                    active: this.step.activate,
                    position: this.step.position,
                    scenario_code: this.step.scenario_code
                })
            })
        },
        findNodeInGraph () {
            AppEventBus.emit(AppEventBusType.SCROLL_TO_ACTIVE_NODE)
        },
        deleteStep () {
            this.$appService.confirm(
                this.$f.tc('confirmDelete', { item: this.step.name })
            ).then(confirm => {
                if (confirm) {
                    this.$store.dispatch(
                        this.$at.scenario.DELETE_STEP,
                        this.step
                    ).then(() => {
                        this.$forceUpdate()
                    })
                }
            })
        },
        cloneStep () {
            this.$appService.useEventBus(
                AppEventBusType.OPEN_STEP_EDITOR,
                {
                    scenario: this.$store.state.scenario.mapCodeToScenario[this.step.scenario_code],
                    cloneStep: this.step
                }
            ).then(data => {
                this.$store.dispatch(this.$at.scenario.CREATE_STEP, data).then(step => {
                    this.$store.dispatch(this.$at.scenario.ACTIVE_DATA, {
                        activeStepCode: step.code
                    })
                })
            })
        },
        onDragEnter (event) {
            event.target.classList.add('active')
        },
        onDragLeave (event) {
            event.target.classList.remove('active')
        },
        onDrop (event, dropCard) {
            this.onDragLeave(event)
            if (!this.waitDrop) {
                return
            }
            let dragCardCode = event.dataTransfer.getData('moveCardCode')
            if (dragCardCode) {
                let dragCard = this.$store.state.scenario.mapCodeToCard[dragCardCode]
                if (dropCard && dropCard.code === dragCard.code) {
                    return
                }
                let cardList = this.$store.state.scenario.mapCodeToStep[dragCard.step_code].cards
                let index = cardList.indexOf(dragCard)
                cardList.splice(index, 1)
                if (dropCard) {
                    index = cardList.indexOf(dropCard)
                    index++
                } else {
                    index = 0
                }
                cardList.splice(index, 0, dragCard)
                cardList.forEach((card, i) => {
                    card.position = i
                })
                this.$store.dispatch(this.$at.scenario.POSITION_CARD, {
                    stepCode: dragCard.step_code,
                    cardCodeList: cardList.map(card => card.code)
                })
            } else {
                let card = {
                    step_code: this.step.code,
                    card_type_id: event.dataTransfer.getData('addCardType'),
                    position: dropCard ? dropCard.position + 1 : 0
                }
                this.$store.dispatch(this.$at.scenario.CREATE_CARD, card)
            }
            this.$forceUpdate()
        }
    },
    watch: {
        shouldUpdateGraph () {
            this.$forceUpdate()
        },
        activeCardCode () {
            if (this.activeCardCode) {
                setTimeout(() => {
                    this.$refs[this.activeCardCode][0].$el.scrollIntoView()
                }, 0)
            }
        },
        step () {
            this.activate = this.step ? !!this.step.activate : this.activate
            this.$refs.body.scrollTop = 0
        }
    }
}
</script>
<style lang="scss" src="./Card/card.scss"></style>
<style scoped lang="scss">
@import "../../../assets/css/fci-var";

$borderColor: #D0D4E2;

.StepContentEditor {
    height: 100%;
    display: flex;
    background: $borderColor1;
}

.step-header {
    padding: 5px 10px;
    border-bottom: 1px solid $borderColor;
    display: flex;
    align-items: center;
    height: 41px;
    flex: 0 0 41px;
}

.step-body {
    flex: 1 1 100px;
    overflow-y: auto;
    scroll-behavior: smooth;
    scroll-padding: 10px;
    padding-top: 10px;
}

.card-editor-container {
    flex: 1 1 100px;
    border-radius: 5px;
    margin-right: 10px;
    background: #fff;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.card-type-container {
    flex: 0 0 80px;
    background: #fff;
    border-radius: 5px 0 0 5px;
    overflow: hidden;
}

.wait-drop {
    .drop-zone {
        border: 1px dashed $primary1;

        &.active {
            background: $primary1;

            i {
                color: #fff;
            }
        }
    }
}

.drop-zone {
    height: 30px;
    margin: 1px 10px;
    border: 1px solid #fff;
    border-radius: 5px;

    i {
        color: $primary1;
        transition: none;
    }
}
</style>
